(() => {
  const ROOT_SELECTOR = '[data-opj-root="main"]';

  const slugify = (value) => value
    .toLocaleLowerCase('de-CH')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const enhanceContent = (content) => {
    const headings = [...content.querySelectorAll('h2')];

    headings.forEach((heading, index) => {
      if (!heading.id) {
        const baseId = slugify(heading.textContent) || `abschnitt-${index + 1}`;
        let candidate = baseId;
        let suffix = 2;

        while (document.getElementById(candidate)) {
          candidate = `${baseId}-${suffix}`;
          suffix += 1;
        }

        heading.id = candidate;
      }
    });

    const firstHeading = headings[0];
    const decisionList = firstHeading?.nextElementSibling;
    const firstHeadingText = firstHeading?.textContent.trim().toLocaleLowerCase('de-CH') || '';

    if (
      decisionList?.matches('ul, ol')
      && /(entscheidung|auf einen blick|kurz gesagt|in \d+ sekunden)/.test(firstHeadingText)
    ) {
      decisionList.classList.add('opj-decision-list');
    }

    headings.forEach((heading) => {
      const headingText = heading.textContent.trim().toLocaleLowerCase('de-CH');

      if (headingText.includes('häufige fragen')) {
        heading.classList.add('opj-faq-heading');
        let sibling = heading.nextElementSibling;

        while (sibling && sibling.tagName !== 'H2') {
          sibling.classList.add('opj-faq-item');
          sibling = sibling.nextElementSibling;
        }
      }

      if (headingText.includes('quellen')) {
        heading.classList.add('opj-sources-heading');
        let sibling = heading.nextElementSibling;

        while (sibling && sibling.tagName !== 'H2') {
          sibling.classList.add('opj-sources-item');
          sibling = sibling.nextElementSibling;
        }
      }
    });

    content.querySelectorAll('figure').forEach((figure) => {
      if (figure.querySelector('a[href*="/products/"]')) {
        figure.classList.add('opj-product-bridge');

        if (!figure.dataset.opjLabel) {
          figure.dataset.opjLabel = 'Passformat prüfen';
        }

        const caption = figure.querySelector('figcaption');
        if (caption) caption.dataset.opjLabel = figure.dataset.opjLabel;
      }
    });

    content.querySelectorAll('table').forEach((table, index) => {
      if (table.parentElement?.classList.contains('opj-table-scroll')) return;

      const wrapper = document.createElement('div');
      const caption = table.querySelector('caption')?.textContent.trim();
      wrapper.className = 'opj-table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', caption || `Tabelle ${index + 1}`);
      const scrollHint = document.createElement('span');
      scrollHint.className = 'opj-table-scroll__hint';
      scrollHint.setAttribute('aria-hidden', 'true');
      scrollHint.textContent = 'Seitlich wischen \u2192';
      table.before(wrapper);
      wrapper.append(scrollHint);
      wrapper.append(table);
    });

    return headings;
  };

  const buildToc = (root, headings) => {
    const tocLists = root.querySelectorAll('[data-opj-toc]');

    if (!headings.length || !tocLists.length) return;

    tocLists.forEach((list) => {
      list.replaceChildren();

      headings.forEach((heading) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
      });
    });

    root.querySelector('[data-opj-toc-shell]')?.classList.add('is-ready');
    root.classList.add('opj-toc-ready');
  };

  const observeHeadings = (root, headings) => {
    if (!('IntersectionObserver' in window) || !headings.length) return;

    const links = [...root.querySelectorAll('[data-opj-toc] a')];
    const setActive = (id) => {
      links.forEach((link) => {
        const isActive = link.hash === `#${id}`;
        link.classList.toggle('is-active', isActive);

        if (isActive) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (visible[0]) setActive(visible[0].target.id);
    }, {
      rootMargin: '-18% 0px -70% 0px',
      threshold: 0
    });

    headings.forEach((heading) => observer.observe(heading));
    root._opjHeadingObserver = observer;
  };

  const init = (root) => {
    if (!root || root.dataset.opjInitialized === 'true') return;

    const content = root.querySelector('[data-opj-content]');
    if (!content) return;

    root.dataset.opjInitialized = 'true';
    const headings = enhanceContent(content);
    const answerNext = document.querySelector('[data-opj-answer-next]');
    const configuredAnswerTarget = answerNext?.dataset.opjAnswerTarget;

    if (answerNext && configuredAnswerTarget) {
      answerNext.href = configuredAnswerTarget;
    } else if (answerNext && headings[0]?.id) {
      answerNext.href = `#${headings[0].id}`;
    }

    buildToc(root, headings);
    observeHeadings(root, headings);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    root?._opjHeadingObserver?.disconnect();
  });
})();
